import React, {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
} from "react";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import {
  AuthContext,
  authReducer,
  AuthDispatchContext,
} from "@context/AuthContext";
import ProtectedRoute from "@routes/ProtectedRoute";
import routes from "@routes/routes";
import NoInternet from "@src/components/NoInternet/NoInternet";
import { loadGtag } from "@src/utils/loadGTag";
import { LoginContext, loginReducer } from "@context/LoginContext";
import useApiHook from "@hooks/useApiHook";
import {
  UserContext,
  UserDispatchContext,
  userReducer,
} from "@context/UserContext";
import {
  LoaderContext,
  LoaderDispatchContext,
  loaderReducer,
} from "@context/LoaderContext";
import Loader from "@components/Loaders/Loader";
import useApiError from "@hooks/useApiError";
import useResizeObserver from "@hooks/useResizeObserver";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "react-datepicker/dist/react-datepicker.css";
import ForgotPasswordLoader from "@components/ForgotPassword/ForgotPasswordLoader";
import { EpisodesContext, episodesReducer } from "@context/EpisodesContext";
import { ProductsContext, productsReducer } from "@context/ProductsContext";
import { MusicsContext, musicsReducer } from "@context/MusicContext";
import {
  CartContext,
  CartIdContext,
  cartIdReducer,
  cartReducer,
} from "@context/CartContext";
import {
  ChatContext,
  ChatDispatchContext,
  ChatIdContext,
  ChatIdDispatchContext,
  chatIdReducer,
  chatReducer,
} from "@context/ChatContext";
import type {
  IConversation,
  IProduct,
  ISingleCartItem,
  IUser,
} from "@src/types/AppTypes";
import { CouponContext, couponReducer } from "@context/CouponContext";
import Cookies from "js-cookie";
import {
  NotificationsContext,
  NotificationsDispatch,
  notificationsReducer,
} from "@context/NotificationsContext";
import {
  CardsContext,
  CardsDisptchContext,
  cardsReducer,
} from "@context/CardsContext";
import {
  ShippingContext,
  ShippingDispatchContext,
  shippingReducer,
} from "@context/ShippingContext";
import {
  SelectedShippingContext,
  SelectedShippingDispatchContext,
  selectedShippingReducer,
} from "@context/SelectedShippingContext";
import { createDB } from "@src/db/EpisodesDb";
import {
  ReportContext,
  ReportDispatchContext,
  reportOptionsReducer,
} from "@context/ReportContext";
import {
  TotalItemsContext,
  TotalItemsDispatchContext,
  totalItemsReducrer,
} from "@context/TotalItemsContext";
import {
  getGuestCartTokenPayloadString,
  setGuestCartToken,
} from "@src/utils/CartItemsCountsHelper";
import useNotification from "@src/hooks/useNotification";
import { VideoManagerProvider } from "@src/context/VideoPlayerManagerProvider";
import DownloadManagerContextProvider from "@src/context/DownloadManagerContextProvider";
import HomeSkeleton from "@src/components/Skeleton/Home/HomeSkeleton";
import { SignupContext, signupReducer } from "@src/context/SignupContext";
import { loadGoogleMaps } from "@src/utils/loadGoogleMaps";
import {
  EmojiContext,
  EmojiDispatchContext,
  emojiReducer,
} from "@src/context/EmojiProduct";
import Profile from "./pages/Profile/Profile";
import PurchasedSkeleton from "./components/Skeleton/Profile/PurchasedSkeleton";
import RentedEpisodesSkeleton from "./components/Skeleton/Profile/RentedEpisodesSkeleton";
import ProfileOrderHistorySkeleton from "./components/Skeleton/Profile/ProfileOrderHistorySkeleton";
import DownloadsSkeleton from "./components/Skeleton/Profile/DownloadsSkeleton";
import WishlistSkeleton from "./components/Skeleton/Profile/WishlistSkeleton";
import BookmarkSkeleton from "./components/Skeleton/Profile/BookmarkSkeleton";
import AddressSkeleton from "./components/Skeleton/Profile/AddressSkeleton";
import PaymentMethodSkeleton from "./components/Skeleton/Profile/PaymentMethodSkeleton";
import NotificationSettingsSkeleton from "./components/Skeleton/Profile/NotificationsSettingsSkeleton";
import AccountSkeleton from "./components/Skeleton/Profile/AccountSkeleton";
import { setAndGetExpiry } from "./utils/ImageExpirtyHelper";
import CartSkeleton from "./components/Skeleton/Cart/CartSkeleton";

const CookieBanner = React.lazy(
  () => import("@src/pages/CookiePolicy/CookieBanner"),
);
const NotFound = React.lazy(() => import("@components/NotFound/NotFound"));
const CommonLayout = React.lazy(
  () => import("@components/CommonLayout/CommonLayout"),
);
const PreOrderPayment = React.lazy(
  () => import("@components/PreOrderPayment/PreOrderPayment"),
);
const AudioPlayerRouteHelper = React.lazy(
  () => import("@components/VideoPlayer/AudioPlayerRouteHelper"),
);
const OrderDetails = React.lazy(
  () => import("@src/components/OrderHistory/OrderDetails"),
);
const OrderReturn = React.lazy(
  () => import("@src/components/OrderHistory/OrderReturn"),
);

const NotificationSettings = React.lazy(
  () => import("@components/NotificationsSettings/NotificationSettings"),
);
const NewsShow = React.lazy(() => import("@pages/News/NewsShow"));
const Purchased = React.lazy(() => import("@components/Purchased/Purchased"));
const RentedEpisodes = React.lazy(
  () => import("@components/Purchased/RentedEpisodes"),
);
const ShowAllMusic = React.lazy(() => import("@pages/Music/ShowAllMusic"));
const ShowAllEpisodes = React.lazy(
  () => import("@pages/Episodes/ShowAllEpisodes"),
);
const Shop = React.lazy(() => import("@pages/Products/Shop"));
const Home = React.lazy(() => import("@pages/Home/Home"));
const Cart = React.lazy(() => import("@pages/Cart/Cart"));
const Product = React.lazy(() => import("@pages/Products/Product"));
const FatsuEmojis = React.lazy(() => import("@pages/FatsuEmojis/FatsuEmojis"));
const Checkout = React.lazy(() => import("@pages/Checkout/Checkout"));
const CompleteCheckout = React.lazy(
  () => import("@pages/Checkout/CompleteCheckout"),
);
const Terms = React.lazy(() => import("@pages/TnC/Terms"));
const Policy = React.lazy(() => import("@pages/PrivacyPolicy/Policy"));
const News = React.lazy(() => import("@pages/News/News"));
const History = React.lazy(() => import("@pages/History/History"));
const Contact = React.lazy(() => import("@pages/Contact/Contact"));
const Wishlist = React.lazy(() => import("@components/Wishlist/Wishlist"));
const Bookmark = React.lazy(() => import("@components/Bookmark/Bookmark"));
const Account = React.lazy(() => import("@components/Account/Account"));
const Downloads = React.lazy(() => import("@components/Downloads/Downloads"));
const PaymentMethod = React.lazy(
  () => import("@components/PaymentMethod/PaymentMethod"),
);
const Address = React.lazy(() => import("@components/Address/Address"));
const OrderHistory = React.lazy(
  () => import("@components/OrderHistory/OrderHistory"),
);
const MessageFatsu = React.lazy(
  () => import("@pages/MessageFatsu/MessageFatsu"),
);
const FatsuOrigin = React.lazy(() => import("@pages/FatsuOrigin/FatsuOrigin"));
const Facts = React.lazy(() => import("@pages/Facts/Facts"));
const Metrics = React.lazy(() => import("@pages/Metrics/Metrics"));
const CookiePolicy = React.lazy(
  () => import("@src/pages/CookiePolicy/CookiePolicy"),
);
import useReactQuery from "./hooks/useReactQuery";
import {
  ChangeEmailContext,
  changeEmailReducer,
} from "./context/ChangeEmailContext";
import SeasonEpisodeCombineSkeleton from "./components/Skeleton/ShowAllEpisode/SeasonEpisodeCombineSkeleton";
import CheckoutSkeleton from "./components/Skeleton/Checkout/CheckoutSkeleton";
import MessageFatsuSkeleton from "./components/Skeleton/MessageFatsu/MessageFatsuSkeleton";
import { useQueryClient } from "@tanstack/react-query";
import FatsuOriginSkeleton from "./components/Skeleton/FatsuOrigin/FatsuOriginSkeleton";
import FactsSkeleton from "./components/Skeleton/Facts/FactsSkeleton";
import MetricsSkeleton from "./components/Skeleton/Metrics/MetricsSkeleton";
import ProductDetailsSkeleton from "./components/Skeleton/Product/ProductDetailsSkeleton";
import FatsuEmojiDetailsSkeleton from "./components/Skeleton/Product/FatsuEmojiDetailsSkeleton";
import InLightNewsSkeleton from "./components/Skeleton/News/InLightNewsSkeleton";
import TermsSkeleton from "./components/Skeleton/Terms/TermsSkeleton";
import ContactSkeleton from "./components/Skeleton/Contact/ContactSkeleton";
const VideoPlayerRouteHelper = React.lazy(
  () => import("@src/components/VideoPlayer/VideoPlayerRouteHelper"),
);

const App: React.FC = () => {
  const [emojiProduct, setEmojiProduct] = useReducer(emojiReducer, null);
  const flashNotification = useNotification();
  const [episodes, setEpisodes] = useReducer(episodesReducer, []);
  const queryClient = useQueryClient();
  const [reportOptions, setReportOptions] = useReducer(
    reportOptionsReducer,
    [],
  );
  const [shippingOptions, setShippingOptions] = useReducer(shippingReducer, []);
  const [selectedShippingId, setSelectedShippingId] = useReducer(
    selectedShippingReducer,
    null,
  );
  const isMobileView = useResizeObserver();
  const setApiError = useApiError();

  const [isAuthenticated, dispatch] = useReducer(
    authReducer,
    !!Cookies.get("aT"),
  );
  const [conversation, setConversation] = useReducer(
    chatReducer,
    {} as IConversation,
  );
  const [conversationId, setConversationId] = useReducer(chatIdReducer, 0);
  const [coupon, setCoupon] = useReducer(couponReducer, {
    couponCode: "",
    isApplied: false,
    discount: "0",
  });
  const [emailData, setEmailData] = useReducer(changeEmailReducer, {
    email: "",
    password: "",
  });
  const [notifications, setNotifications] = useReducer(
    notificationsReducer,
    [],
  );
  const [totalItems, setTotalItems] = useReducer(totalItemsReducrer, 0);

  const audioListApiEndPoint = useApiHook({ method: "get", url: "/musics" });

  const [cartItems, setCartItems] = useReducer(cartReducer, []);
  const [cartId, setCartId] = useReducer(cartIdReducer, 0);

  const [user, userDispatch] = useReducer(userReducer, null);
  const [loader, setLoader] = useReducer(loaderReducer, false);
  const [cards, setCards] = useReducer(cardsReducer, []);

  const emojiEndpoint = useApiHook({
    url: "/emoji_products",
    method: "get",
  });

  const [musics, setMusics] = useReducer(musicsReducer, []);

  const [products, setProducts] = useReducer(productsReducer, []);
  const getCartProductsEndPoint = useApiHook({
    method: "get",
    url: "/guest_users/get_cart",
  });
  const getUserEndPoint = useApiHook({
    method: "get",
    url: "/users/user_details",
  });
  const addToCartApiEndPoint = useApiHook({
    method: "post",
    url: "/carts/add_to_cart",
  });
  const checkMusicEpisodeApiEndPoint = useApiHook({
    method: "post",
    url: "/users/check_music_and_episode_added",
  });

  const addToWishListEndPoint = useApiHook({
    method: "post",
    url: "/wishlists",
  });

  const [login, setLogin] = useReducer(loginReducer, false);
  const [signup, setSignup] = useReducer(signupReducer, false);

  const signupContextValue = useMemo(
    () => ({ signup, setSignup }),
    [signup, setSignup],
  );

  const loginContextValue = useMemo(
    () => ({ login, setLogin }),
    [login, setLogin],
  );

  const notificationContextValue = useMemo(
    () => ({ notifications }),
    [notifications],
  );

  const couponContextValue = useMemo(
    () => ({ coupon, setCoupon }),
    [coupon, setCoupon],
  );

  const emailDataContextValue = useMemo(
    () => ({ emailData, setEmailData }),
    [emailData, setEmailData],
  );

  const cartContextValue = useMemo(
    () => ({ cart: cartItems, setCartItems }),
    [cartItems, setCartItems],
  );

  const cartIdContextValue = useMemo(
    () => ({ cartId, setCartId }),
    [cartId, setCartId],
  );

  const episodesContextValue = useMemo(
    () => ({ episodes, setEpisodes }),
    [episodes, setEpisodes],
  );

  const musicContextValue = useMemo(
    () => ({ musics, setMusics }),
    [musics, setMusics],
  );
  const productsContextValue = useMemo(
    () => ({ products, setProducts }),
    [products, setProducts],
  );

  const handleEpisodeAddToCart = useCallback(
    async (itemId: number, episodeType: string, userCartId: number) => {
      try {
        const res = await addToCartApiEndPoint<{
          status: boolean;
          message: string;
          guest_token: string;
          cart_item: ISingleCartItem;
        }>(
          {
            cart_item: {
              quantity: 1,
              itemable_type: "Episode",
              itemable_id: itemId,
              cart_id: userCartId,
              purchase_type: episodeType,
            },
          },
          getGuestCartTokenPayloadString(),
        );
        if (res) {
          if (res.data.guest_token) {
            setGuestCartToken(res.data.guest_token);
          }

          setTotalItems({
            payload: 1,
            type: "updateTotalCount",
          });

          flashNotification({
            status: true,
            message: "Episode added to cart",
          });
          localStorage.removeItem("lID");
          localStorage.removeItem("letype");
          localStorage.removeItem("lesubtype");
        }
      } catch (e: any) {
        setApiError(e);
      } finally {
        localStorage.removeItem("lID");
        localStorage.removeItem("letype");
        localStorage.removeItem("lesubtype");
      }
    },
    [addToCartApiEndPoint, flashNotification, setApiError],
  );
  const handleMusicAddToCart = useCallback(
    async (id: number, userCardId: number) => {
      try {
        const res = await addToCartApiEndPoint<{
          status: boolean;
          message: string;
          cart_item: ISingleCartItem;
          guest_token: string;
        }>(
          {
            cart_item: {
              quantity: 1,
              itemable_type: "Music",
              itemable_id: id,
              cart_id: userCardId,
              purchase_type: "purchase",
            },
          },
          getGuestCartTokenPayloadString(),
        );
        if (res) {
          if (res.data.guest_token) {
            setGuestCartToken(res.data.guest_token);
          }
          setTotalItems({
            payload: 1,
            type: "updateTotalCount",
          });
          flashNotification({
            status: true,
            message: "Music added to cart",
          });
          localStorage.removeItem("lID");
          localStorage.removeItem("letype");
          localStorage.removeItem("lesubtype");
        }
      } catch (e: any) {
        setApiError(e);
      } finally {
        localStorage.removeItem("lID");
        localStorage.removeItem("letype");
        localStorage.removeItem("lesubtype");
      }
    },
    [addToCartApiEndPoint, flashNotification, setApiError],
  );

  const handleCheckMusicEpisode = useCallback(
    async (id: number, type: string, userCartId: number) => {
      try {
        const res = await checkMusicEpisodeApiEndPoint<{
          status: boolean;
          added_to_cart: boolean;
          is_purchased: boolean;
          is_rented: boolean;
        }>(null, `?id=${id}&type=${type}`, null);

        if (!res) return;

        const { is_purchased, added_to_cart, is_rented } = res.data;

        // If already purchased or already in cart → do nothing
        if (is_purchased || added_to_cart) return;

        if (type === "Episode") {
          // Episode-special case: rented also blocks action
          if (is_rented) return;

          const lesubtype = localStorage.getItem("lesubtype") || "";
          handleEpisodeAddToCart(id, lesubtype, userCartId);
          return;
        }

        // Music type
        handleMusicAddToCart(id, userCartId);
      } catch (e: any) {
        setApiError(e);
      }
    },
    [
      checkMusicEpisodeApiEndPoint,
      handleEpisodeAddToCart,
      handleMusicAddToCart,
      setApiError,
    ],
  );

  // this is for add to wishlist music/episode, if user has clicked on episode/music add to wishlist button in prelogin mode
  const handleAddEpisodeMusicToWishlist = useCallback(
    async (
      id: number,
      type: "Music" | "Episode" | "Product",
      seasonNumber: number = -1,
      variantId: number = -1,
    ) => {
      const clearSelections = () => {
        sessionStorage.removeItem("selMusId");
        sessionStorage.removeItem("selEpId");
        sessionStorage.removeItem("selProId");
        sessionStorage.removeItem("selProVarId");
      };

      const updateMusicWishlist = () => {
        const next = [...musics];
        const index = next.findIndex((i) => i.id === id);
        if (index > -1 && !next[index].is_wishlist) {
          next[index] = { ...next[index], is_wishlist: true };
        }
      };

      const updateEpisodeWishlist = () => {
        if (!seasonNumber || !episodes[seasonNumber]) return;
        const nextEpisodes = [...episodes[seasonNumber]];
        const index = nextEpisodes.findIndex((i) => i.id === id);
        if (index > -1 && !nextEpisodes[index].is_wishlist) {
          nextEpisodes[index].is_wishlist = true;
        }
        setEpisodes({
          type: "updateEpisodes",
          payload: { ...episodes, [seasonNumber]: nextEpisodes },
        });
      };

      const invalidateProductQueries = () => {
        queryClient.invalidateQueries({ queryKey: ["get-wishlist"] });
        queryClient.invalidateQueries({
          queryKey: ["get-products-by-category"],
        });
        queryClient.invalidateQueries({
          queryKey: ["get-product-details", isAuthenticated],
        });
      };

      const runWishlistUpdate = () => {
        switch (type) {
          case "Music":
            updateMusicWishlist();
            return;
          case "Episode":
            updateEpisodeWishlist();
            return;
          case "Product":
            invalidateProductQueries();
            return;
        }
      };

      try {
        clearSelections();

        const payload = {
          listable_id: id,
          listable_type: type,
          variant_id: variantId === -1 ? undefined : variantId,
        };

        const res = await addToWishListEndPoint<{
          status: boolean;
          message: string;
        }>(payload);

        if (!res?.data) return;

        runWishlistUpdate();

        flashNotification(res.data);
        queryClient.invalidateQueries({ queryKey: ["get-wishlist"] });
      } catch (e: any) {
        clearSelections();
        setApiError(e);
      }
    },
    [
      addToWishListEndPoint,
      setApiError,
      flashNotification,
      queryClient,
      episodes,
      musics,
      isAuthenticated,
    ],
  );

  useEffect(() => {
    if (!isAuthenticated) return;

    const applyPendingWishlistActions = async () => {
      const mus = sessionStorage.getItem("selMusId");
      const ep = sessionStorage.getItem("selEpId");
      const pro = sessionStorage.getItem("selProId");
      const proVar = sessionStorage.getItem("selProVarId");

      if (mus) {
        await handleAddEpisodeMusicToWishlist(JSON.parse(mus), "Music");
      }

      if (ep) {
        const parsed = JSON.parse(ep);
        if (parsed?.episodeId) {
          await handleAddEpisodeMusicToWishlist(
            parsed.episodeId,
            "Episode",
            parsed.seasonNumber,
          );
        }
      }

      if (pro) {
        const variant = proVar ? JSON.parse(proVar) : -1;
        await handleAddEpisodeMusicToWishlist(+pro, "Product", 0, variant);
      }
    };

    const applyPendingEpisodeCheck = (user: IUser) => {
      const iId = localStorage.getItem("lID");
      const letype = localStorage.getItem("letype");
      if (Number(iId) && letype) {
        handleCheckMusicEpisode(Number(iId), letype, user.cart_id);
      }
    };

    const handleUserDetails = async () => {
      try {
        const res = await getUserEndPoint<{
          status: boolean;
          message: string;
          user: IUser;
        }>(null, "", null);

        const user = res?.data?.user;
        if (!res?.data.status || !user) return;

        if (user.id) {
          Cookies.set("UIDT", user.id.toString(), {
            expires: 7 * 24 * 60 * 60,
          });
        }

        userDispatch({ type: "setUser", payload: user });

        // pending wishlist actions triggered before login
        await applyPendingWishlistActions();

        // pending "check episode/music" action triggered before login
        applyPendingEpisodeCheck(user);

        setTotalItems({
          payload: user.total_items,
          type: "setTotalCount",
        });

        createDB(`DB_${user.id}`);
      } catch (e: any) {
        setApiError(e);
      }
    };

    handleUserDetails();
  }, [
    isAuthenticated,
    getUserEndPoint,
    setApiError,
    handleAddEpisodeMusicToWishlist,
    handleCheckMusicEpisode,
  ]);

  useEffect(() => {
    //for image expiry
    setAndGetExpiry();
  }, []);

  useEffect(() => {
    const script = document.createElement("script");
    script.src =
      "https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";

    window.googleTranslateElementInit = () => {
      if (window.google?.translate?.TranslateElement) {
        try {
          new window.google.translate.TranslateElement(
            {
              pageLanguage: "en",
            },
            "google_translate_element",
          );
        } catch (e) {
          console.error("Google Translate initialization failed", e);
        }
      }
    };

    document.body.appendChild(script);

    return () => {
      script.remove();
      delete window.googleTranslateElementInit;
    };
  }, [isAuthenticated, isMobileView]);

  const { data, error, isSuccess } = useReactQuery<{
    status: boolean;
    message: string;
    musics: any[];
  }>({
    args: { queryKey: ["get-music-list", isAuthenticated] },
    queryFn: () =>
      audioListApiEndPoint(
        null,
        isAuthenticated ? "?user_id=" + user!.id : "",
        {
          headers: {
            "X-Random-Seed": localStorage.getItem("hxrs") ?? 0, // hxrs means home page x-random-seed,
          },
        },
      ),
  });

  useEffect(() => {
    if (isSuccess && data) {
      // these both line for music shuffle
      const randomSeed = data?.headers["x-random-seed"];
      if (randomSeed) localStorage.setItem("hxrs", randomSeed.toString());
      setMusics({
        type: "updateMusics",
        payload: data.data.musics,
      });
    } else if (error) {
      setApiError(error);
    }
  }, [isSuccess, data, error, setApiError]);

  const {
    data: emojiData,
    error: emojiError,
    isSuccess: emojiIsSuccess,
  } = useReactQuery<{
    product: IProduct;
  }>({
    args: { queryKey: ["get-emoji", isAuthenticated] },
    queryFn: () => emojiEndpoint(null, "", null),
  });

  useEffect(() => {
    if (emojiIsSuccess && emojiData) {
      if (emojiData?.data.product) {
        setEmojiProduct({
          type: "updateEmoji",
          payload: emojiData.data.product,
        });
      }
    } else if (emojiError) {
      setApiError(emojiError);
    }
  }, [emojiIsSuccess, emojiData, emojiError, setApiError, isAuthenticated]);

  const {
    data: cartData,
    error: cartError,
    isSuccess: cartIsSuccess,
  } = useReactQuery<{
    cart_items: ISingleCartItem[];
    id: number;
  }>({
    args: { queryKey: ["get-guest-cart-item"], enabled: !isAuthenticated },
    queryFn: () =>
      getCartProductsEndPoint(null, getGuestCartTokenPayloadString(), null),
  });

  useEffect(() => {
    if (cartIsSuccess && cartData) {
      // for storing guest user cart id
      setCartId({ type: "guestUserCartId", payload: cartData.data.id });

      // this is for guest user
      setTotalItems({
        payload: cartData.data.cart_items
          .map((val) => val.quantity)
          .reduce((val, acc) => acc + val, 0),
        type: "setTotalCount",
      });
    } else if (cartError) {
      setApiError(cartError);
    }
  }, [cartIsSuccess, cartData, cartError, setApiError]);

  useEffect(() => {
    // removed this keys here, if user click on wishlist button and referesh the page, then need to clear stored id
    sessionStorage.removeItem("selMusId"); //remove selected music id, which we stored before login
    sessionStorage.removeItem("selEpId"); //remove selected episode id, which we stored before login
    sessionStorage.removeItem("selProId"); //remove selected product id, which we stored before login
    sessionStorage.removeItem("selProVarId"); //remove selected productVariant id, which we stored before login

    const bannerAccepted = !!Cookies.get("cookieBanner");
    if (bannerAccepted) {
      loadGtag();
    }

    loadGoogleMaps().catch((error) => {
      console.error("Error loading Google Maps API:", error);
    });
  }, []);

  useEffect(() => {
    if (!user?.id) return;
    navigator?.serviceWorker?.ready?.then(() => {
      if (navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({
          message: "SyncDB",
          id: user.id,
        });
      } else {
        console.warn("No active service worker found.");
      }
    });
  }, [user?.id]);

  useEffect(() => {
    for (const el of document.querySelectorAll(".userway_buttons_wrapper")) {
      el.removeAttribute("style"); // Removes all inline styles
    }
  }, []);

  useEffect(() => {
    let timerRef: NodeJS.Timeout | null = null;
    const adjustWidgetPosition = () => {
      const widget = document.querySelector(
        ".userway_buttons_wrapper",
      ) as HTMLDivElement;
      if (widget) {
        const styleTag = document.createElement("style");
        styleTag.innerHTML = `
  .userway_buttons_wrapper {
    bottom: 10px !important;
    right: auto !important;
    top: auto !important;
    left: 10px !important;
    transform: none !important;
  }
`;
        document.head.appendChild(styleTag);

        if (timerRef) clearInterval(timerRef);
      }
    };

    timerRef = setInterval(adjustWidgetPosition, 500);

    return () => clearInterval(timerRef);
  }, []);

  return (
    <div>
      <Router>
        {/* <Suspense fallback={<Loader />}> */}
        <VideoManagerProvider>
          <DownloadManagerContextProvider>
            <AuthContext.Provider value={isAuthenticated}>
              <AuthDispatchContext.Provider value={dispatch}>
                <SignupContext.Provider value={signupContextValue}>
                  <EmojiContext.Provider value={emojiProduct}>
                    <EmojiDispatchContext.Provider value={setEmojiProduct}>
                      <LoginContext.Provider value={loginContextValue}>
                        <UserContext.Provider value={user}>
                          <UserDispatchContext.Provider value={userDispatch}>
                            <ChangeEmailContext.Provider
                              value={emailDataContextValue}
                            >
                              <TotalItemsContext.Provider value={totalItems}>
                                <TotalItemsDispatchContext.Provider
                                  value={setTotalItems}
                                >
                                  <ShippingContext.Provider
                                    value={shippingOptions}
                                  >
                                    <ShippingDispatchContext.Provider
                                      value={setShippingOptions}
                                    >
                                      <SelectedShippingContext.Provider
                                        value={selectedShippingId}
                                      >
                                        <SelectedShippingDispatchContext.Provider
                                          value={setSelectedShippingId}
                                        >
                                          <CardsContext.Provider value={cards}>
                                            <CardsDisptchContext.Provider
                                              value={setCards}
                                            >
                                              <NotificationsDispatch.Provider
                                                value={setNotifications}
                                              >
                                                <NotificationsContext.Provider
                                                  value={
                                                    notificationContextValue
                                                  }
                                                >
                                                  <CouponContext.Provider
                                                    value={couponContextValue}
                                                  >
                                                    <CartContext.Provider
                                                      value={cartContextValue}
                                                    >
                                                      <CartIdContext.Provider
                                                        value={
                                                          cartIdContextValue
                                                        }
                                                      >
                                                        <EpisodesContext.Provider
                                                          value={
                                                            episodesContextValue
                                                          }
                                                        >
                                                          <MusicsContext.Provider
                                                            value={
                                                              musicContextValue
                                                            }
                                                          >
                                                            <ProductsContext.Provider
                                                              value={
                                                                productsContextValue
                                                              }
                                                            >
                                                              <LoaderContext.Provider
                                                                value={loader}
                                                              >
                                                                <LoaderDispatchContext.Provider
                                                                  value={
                                                                    setLoader
                                                                  }
                                                                >
                                                                  <ReportContext.Provider
                                                                    value={
                                                                      reportOptions
                                                                    }
                                                                  >
                                                                    <ReportDispatchContext.Provider
                                                                      value={
                                                                        setReportOptions
                                                                      }
                                                                    >
                                                                      <Routes>
                                                                        {/* <Route
                                                                          path={
                                                                            routes.fatsoji
                                                                          }
                                                                          element={
                                                                            <Fatsoji />
                                                                          }
                                                                        /> */}
                                                                        <Route
                                                                          path={
                                                                            routes.payments
                                                                          }
                                                                          element={
                                                                            <Suspense
                                                                              fallback={
                                                                                <Loader />
                                                                              }
                                                                            >
                                                                              <PreOrderPayment />
                                                                            </Suspense>
                                                                          }
                                                                        ></Route>
                                                                        <Route
                                                                          path={
                                                                            routes.forgotPass
                                                                          }
                                                                          element={
                                                                            <Suspense
                                                                              fallback={
                                                                                <Loader />
                                                                              }
                                                                            >
                                                                              <ForgotPasswordLoader />
                                                                            </Suspense>
                                                                          }
                                                                        />
                                                                        <Route
                                                                          element={
                                                                            <CommonLayout />
                                                                          }
                                                                        >
                                                                          <Route
                                                                            path={
                                                                              routes.default
                                                                            }
                                                                            index
                                                                            element={
                                                                              <ChatContext.Provider
                                                                                value={
                                                                                  conversation
                                                                                }
                                                                              >
                                                                                <ChatDispatchContext.Provider
                                                                                  value={
                                                                                    setConversation
                                                                                  }
                                                                                >
                                                                                  <ChatIdContext.Provider
                                                                                    value={
                                                                                      conversationId
                                                                                    }
                                                                                  >
                                                                                    <ChatIdDispatchContext.Provider
                                                                                      value={
                                                                                        setConversationId
                                                                                      }
                                                                                    >
                                                                                      {navigator.onLine ? (
                                                                                        <Suspense
                                                                                          fallback={
                                                                                            <HomeSkeleton />
                                                                                          }
                                                                                        >
                                                                                          <Home />
                                                                                        </Suspense>
                                                                                      ) : (
                                                                                        <NoInternet />
                                                                                      )}
                                                                                    </ChatIdDispatchContext.Provider>
                                                                                  </ChatIdContext.Provider>
                                                                                </ChatDispatchContext.Provider>
                                                                              </ChatContext.Provider>
                                                                            }
                                                                          />
                                                                          <Route
                                                                            path={
                                                                              routes.music
                                                                            }
                                                                            element={
                                                                              <Suspense>
                                                                                <AudioPlayerRouteHelper />
                                                                              </Suspense>
                                                                            }
                                                                          />
                                                                          <Route
                                                                            path={
                                                                              routes.episode
                                                                            }
                                                                            element={
                                                                              <Suspense>
                                                                                <VideoPlayerRouteHelper />
                                                                              </Suspense>
                                                                            }
                                                                          />
                                                                          <Route
                                                                            path={
                                                                              routes.product
                                                                            }
                                                                            element={
                                                                              <ChatContext.Provider
                                                                                value={
                                                                                  conversation
                                                                                }
                                                                              >
                                                                                <ChatDispatchContext.Provider
                                                                                  value={
                                                                                    setConversation
                                                                                  }
                                                                                >
                                                                                  <ChatIdContext.Provider
                                                                                    value={
                                                                                      conversationId
                                                                                    }
                                                                                  >
                                                                                    <ChatIdDispatchContext.Provider
                                                                                      value={
                                                                                        setConversationId
                                                                                      }
                                                                                    >
                                                                                      <Suspense
                                                                                        fallback={
                                                                                          <ProductDetailsSkeleton />
                                                                                        }
                                                                                      >
                                                                                        <Product />
                                                                                      </Suspense>
                                                                                    </ChatIdDispatchContext.Provider>
                                                                                  </ChatIdContext.Provider>
                                                                                </ChatDispatchContext.Provider>
                                                                              </ChatContext.Provider>
                                                                            }
                                                                          />
                                                                          <Route
                                                                            path={
                                                                              routes.fatsuEmojis
                                                                            }
                                                                            element={
                                                                              <ChatContext.Provider
                                                                                value={
                                                                                  conversation
                                                                                }
                                                                              >
                                                                                <ChatDispatchContext.Provider
                                                                                  value={
                                                                                    setConversation
                                                                                  }
                                                                                >
                                                                                  <ChatIdContext.Provider
                                                                                    value={
                                                                                      conversationId
                                                                                    }
                                                                                  >
                                                                                    <ChatIdDispatchContext.Provider
                                                                                      value={
                                                                                        setConversationId
                                                                                      }
                                                                                    >
                                                                                      <Suspense
                                                                                        fallback={
                                                                                          <FatsuEmojiDetailsSkeleton />
                                                                                        }
                                                                                      >
                                                                                        <FatsuEmojis />
                                                                                      </Suspense>
                                                                                    </ChatIdDispatchContext.Provider>
                                                                                  </ChatIdContext.Provider>
                                                                                </ChatDispatchContext.Provider>
                                                                              </ChatContext.Provider>
                                                                            }
                                                                          />
                                                                          <Route
                                                                            path={
                                                                              routes.shop
                                                                            }
                                                                            element={
                                                                              <Suspense
                                                                                fallback={
                                                                                  <Loader />
                                                                                }
                                                                              >
                                                                                <Shop />
                                                                              </Suspense>
                                                                            }
                                                                          />
                                                                          <Route
                                                                            path={
                                                                              routes.news
                                                                            }
                                                                            element={
                                                                              <Suspense
                                                                                fallback={
                                                                                  <div className="container max-md:px-0">
                                                                                    <div className="text-white min-h-[900px] relative">
                                                                                      <InLightNewsSkeleton />
                                                                                    </div>
                                                                                  </div>
                                                                                }
                                                                              >
                                                                                <News />
                                                                              </Suspense>
                                                                            }
                                                                          />
                                                                          <Route
                                                                            path={
                                                                              routes.newsShow
                                                                            }
                                                                            element={
                                                                              <Suspense>
                                                                                <NewsShow />
                                                                              </Suspense>
                                                                            }
                                                                          />
                                                                          <Route
                                                                            path={
                                                                              routes.messages
                                                                            }
                                                                            element={
                                                                              <Suspense
                                                                                fallback={
                                                                                  <MessageFatsuSkeleton />
                                                                                }
                                                                              >
                                                                                <MessageFatsu />
                                                                              </Suspense>
                                                                            }
                                                                          />
                                                                          <Route
                                                                            path={
                                                                              routes.fatsuOrigin
                                                                            }
                                                                            element={
                                                                              <Suspense
                                                                                fallback={
                                                                                  <FatsuOriginSkeleton />
                                                                                }
                                                                              >
                                                                                <FatsuOrigin />
                                                                              </Suspense>
                                                                            }
                                                                          />
                                                                          <Route
                                                                            path={
                                                                              routes.facts
                                                                            }
                                                                            element={
                                                                              <Suspense
                                                                                fallback={
                                                                                  <FactsSkeleton />
                                                                                }
                                                                              >
                                                                                <Facts />
                                                                              </Suspense>
                                                                            }
                                                                          />
                                                                          <Route
                                                                            path={
                                                                              routes.history
                                                                            }
                                                                            element={
                                                                              <Suspense>
                                                                                <History />
                                                                              </Suspense>
                                                                            }
                                                                          />
                                                                          <Route
                                                                            path={
                                                                              routes.metrics
                                                                            }
                                                                            element={
                                                                              <Suspense
                                                                                fallback={
                                                                                  <MetricsSkeleton />
                                                                                }
                                                                              >
                                                                                <Metrics />
                                                                              </Suspense>
                                                                            }
                                                                          />
                                                                          <Route
                                                                            path={
                                                                              routes.allEpisodes
                                                                            }
                                                                            element={
                                                                              <ChatContext.Provider
                                                                                value={
                                                                                  conversation
                                                                                }
                                                                              >
                                                                                <ChatDispatchContext.Provider
                                                                                  value={
                                                                                    setConversation
                                                                                  }
                                                                                >
                                                                                  <ChatIdContext.Provider
                                                                                    value={
                                                                                      conversationId
                                                                                    }
                                                                                  >
                                                                                    <ChatIdDispatchContext.Provider
                                                                                      value={
                                                                                        setConversationId
                                                                                      }
                                                                                    >
                                                                                      <Suspense
                                                                                        fallback={
                                                                                          <SeasonEpisodeCombineSkeleton type="episode" />
                                                                                        }
                                                                                      >
                                                                                        <ShowAllEpisodes />
                                                                                      </Suspense>
                                                                                    </ChatIdDispatchContext.Provider>
                                                                                  </ChatIdContext.Provider>
                                                                                </ChatDispatchContext.Provider>
                                                                              </ChatContext.Provider>
                                                                            }
                                                                          />
                                                                          <Route
                                                                            path={
                                                                              routes.allMusic
                                                                            }
                                                                            element={
                                                                              <Suspense
                                                                                fallback={
                                                                                  <SeasonEpisodeCombineSkeleton type="music" />
                                                                                }
                                                                              >
                                                                                <ShowAllMusic />
                                                                              </Suspense>
                                                                            }
                                                                          />
                                                                          <Route
                                                                            path={
                                                                              routes.terms
                                                                            }
                                                                            element={
                                                                              <Suspense
                                                                                fallback={
                                                                                  <TermsSkeleton />
                                                                                }
                                                                              >
                                                                                <Terms />
                                                                              </Suspense>
                                                                            }
                                                                          />
                                                                          <Route
                                                                            path={
                                                                              routes.policy
                                                                            }
                                                                            element={
                                                                              <Suspense
                                                                                fallback={
                                                                                  <TermsSkeleton />
                                                                                }
                                                                              >
                                                                                <Policy />
                                                                              </Suspense>
                                                                            }
                                                                          />
                                                                          <Route
                                                                            path={
                                                                              routes.cookiePolicy
                                                                            }
                                                                            element={
                                                                              <Suspense
                                                                                fallback={
                                                                                  <TermsSkeleton />
                                                                                }
                                                                              >
                                                                                <CookiePolicy />
                                                                              </Suspense>
                                                                            }
                                                                          />
                                                                          <Route
                                                                            path={
                                                                              routes.cart
                                                                            }
                                                                            element={
                                                                              <Suspense
                                                                                fallback={
                                                                                  <CartSkeleton />
                                                                                }
                                                                              >
                                                                                <Cart />
                                                                              </Suspense>
                                                                            }
                                                                          />
                                                                          <Route
                                                                            path={
                                                                              routes.checkout
                                                                            }
                                                                            element={
                                                                              <Suspense
                                                                                fallback={
                                                                                  <CheckoutSkeleton />
                                                                                }
                                                                              >
                                                                                <Checkout />
                                                                              </Suspense>
                                                                            }
                                                                          />
                                                                          <Route
                                                                            path={
                                                                              routes.complete
                                                                            }
                                                                            element={
                                                                              <Suspense>
                                                                                <CompleteCheckout />
                                                                              </Suspense>
                                                                            }
                                                                          />
                                                                          <Route
                                                                            path={
                                                                              routes.contact
                                                                            }
                                                                            element={
                                                                              <Suspense
                                                                                fallback={
                                                                                  <ContactSkeleton />
                                                                                }
                                                                              >
                                                                                <Contact />
                                                                              </Suspense>
                                                                            }
                                                                          />
                                                                          <Route
                                                                            element={
                                                                              <ProtectedRoute />
                                                                            }
                                                                          >
                                                                            <Route
                                                                              element={
                                                                                <Profile />
                                                                              }
                                                                            >
                                                                              <Route
                                                                                index
                                                                                path={
                                                                                  routes.account
                                                                                }
                                                                                element={
                                                                                  <Suspense
                                                                                    fallback={
                                                                                      <AccountSkeleton />
                                                                                    }
                                                                                  >
                                                                                    <Account />
                                                                                  </Suspense>
                                                                                }
                                                                              />
                                                                              <Route
                                                                                path={
                                                                                  routes.purchased
                                                                                }
                                                                                element={
                                                                                  <Suspense
                                                                                    fallback={
                                                                                      <PurchasedSkeleton />
                                                                                    }
                                                                                  >
                                                                                    <Purchased />
                                                                                  </Suspense>
                                                                                }
                                                                              />
                                                                              <Route
                                                                                path={
                                                                                  routes.rentedEpisodes
                                                                                }
                                                                                element={
                                                                                  <Suspense
                                                                                    fallback={
                                                                                      <RentedEpisodesSkeleton />
                                                                                    }
                                                                                  >
                                                                                    <RentedEpisodes />
                                                                                  </Suspense>
                                                                                }
                                                                              />
                                                                              <Route
                                                                                path={
                                                                                  routes.orderHistory
                                                                                }
                                                                                element={
                                                                                  <Suspense
                                                                                    fallback={
                                                                                      <ProfileOrderHistorySkeleton />
                                                                                    }
                                                                                  >
                                                                                    <OrderHistory />
                                                                                  </Suspense>
                                                                                }
                                                                              >
                                                                                <Route
                                                                                  path={
                                                                                    routes.orderDetails +
                                                                                    "/:orderId"
                                                                                  }
                                                                                  element={
                                                                                    <Suspense
                                                                                      fallback={
                                                                                        <Loader />
                                                                                      }
                                                                                    >
                                                                                      <OrderDetails />
                                                                                    </Suspense>
                                                                                  }
                                                                                ></Route>
                                                                                <Route
                                                                                  path={
                                                                                    routes.orderReturn +
                                                                                    "/:orderId"
                                                                                  }
                                                                                  element={
                                                                                    <Suspense
                                                                                      fallback={
                                                                                        <Loader />
                                                                                      }
                                                                                    >
                                                                                      <OrderReturn />
                                                                                    </Suspense>
                                                                                  }
                                                                                />
                                                                              </Route>

                                                                              <Route
                                                                                path={
                                                                                  routes.wishlist
                                                                                }
                                                                                element={
                                                                                  <ChatContext.Provider
                                                                                    value={
                                                                                      conversation
                                                                                    }
                                                                                  >
                                                                                    <ChatDispatchContext.Provider
                                                                                      value={
                                                                                        setConversation
                                                                                      }
                                                                                    >
                                                                                      <ChatIdContext.Provider
                                                                                        value={
                                                                                          conversationId
                                                                                        }
                                                                                      >
                                                                                        <ChatIdDispatchContext.Provider
                                                                                          value={
                                                                                            setConversationId
                                                                                          }
                                                                                        >
                                                                                          <Suspense
                                                                                            fallback={
                                                                                              <WishlistSkeleton />
                                                                                            }
                                                                                          >
                                                                                            <Wishlist />
                                                                                          </Suspense>
                                                                                        </ChatIdDispatchContext.Provider>
                                                                                      </ChatIdContext.Provider>
                                                                                    </ChatDispatchContext.Provider>
                                                                                  </ChatContext.Provider>
                                                                                }
                                                                              />
                                                                              <Route
                                                                                path={
                                                                                  routes.bookmark
                                                                                }
                                                                                element={
                                                                                  <Suspense
                                                                                    fallback={
                                                                                      <BookmarkSkeleton />
                                                                                    }
                                                                                  >
                                                                                    <Bookmark />
                                                                                  </Suspense>
                                                                                }
                                                                              />
                                                                              <Route
                                                                                path={
                                                                                  routes.addresses
                                                                                }
                                                                                element={
                                                                                  <Suspense
                                                                                    fallback={
                                                                                      <AddressSkeleton />
                                                                                    }
                                                                                  >
                                                                                    <Address />
                                                                                  </Suspense>
                                                                                }
                                                                              />
                                                                              <Route
                                                                                path={
                                                                                  routes.downloads
                                                                                }
                                                                                element={
                                                                                  <Suspense
                                                                                    fallback={
                                                                                      <DownloadsSkeleton />
                                                                                    }
                                                                                  >
                                                                                    <Downloads />
                                                                                  </Suspense>
                                                                                }
                                                                              />
                                                                              <Route
                                                                                path={
                                                                                  routes.paymentmethod
                                                                                }
                                                                                element={
                                                                                  <Suspense
                                                                                    fallback={
                                                                                      <PaymentMethodSkeleton />
                                                                                    }
                                                                                  >
                                                                                    <PaymentMethod />
                                                                                  </Suspense>
                                                                                }
                                                                              />
                                                                              <Route
                                                                                path={
                                                                                  routes.notificationSettings
                                                                                }
                                                                                element={
                                                                                  <Suspense
                                                                                    fallback={
                                                                                      <NotificationSettingsSkeleton />
                                                                                    }
                                                                                  >
                                                                                    <NotificationSettings />
                                                                                  </Suspense>
                                                                                }
                                                                              />
                                                                            </Route>
                                                                          </Route>
                                                                        </Route>
                                                                        <Route
                                                                          path="*"
                                                                          element={
                                                                            <NotFound />
                                                                          }
                                                                        />
                                                                      </Routes>
                                                                    </ReportDispatchContext.Provider>
                                                                  </ReportContext.Provider>
                                                                  {/* </Suspense> */}
                                                                </LoaderDispatchContext.Provider>
                                                              </LoaderContext.Provider>
                                                            </ProductsContext.Provider>
                                                          </MusicsContext.Provider>
                                                        </EpisodesContext.Provider>
                                                      </CartIdContext.Provider>
                                                    </CartContext.Provider>
                                                  </CouponContext.Provider>
                                                </NotificationsContext.Provider>
                                              </NotificationsDispatch.Provider>
                                            </CardsDisptchContext.Provider>
                                          </CardsContext.Provider>
                                        </SelectedShippingDispatchContext.Provider>
                                      </SelectedShippingContext.Provider>
                                    </ShippingDispatchContext.Provider>
                                  </ShippingContext.Provider>
                                </TotalItemsDispatchContext.Provider>
                              </TotalItemsContext.Provider>
                            </ChangeEmailContext.Provider>
                          </UserDispatchContext.Provider>
                        </UserContext.Provider>
                      </LoginContext.Provider>
                    </EmojiDispatchContext.Provider>
                  </EmojiContext.Provider>
                </SignupContext.Provider>
              </AuthDispatchContext.Provider>
            </AuthContext.Provider>
          </DownloadManagerContextProvider>
        </VideoManagerProvider>
        <Suspense fallback={null}>
          <CookieBanner />
        </Suspense>
      </Router>
    </div>
  );
};

export default App;
